import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AtSign, Copy, KeyRound, ShieldAlert } from "lucide-react";
import { Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";

export const TempPasswordBanner = () => {
  const { t } = usePrefs();
  const { user } = useAuth();
  if (!user || !user.temp_password) return null;
  return (
    <Link to="/venue/settings#security" data-testid="temp-password-banner"
      className="mb-5 flex items-center gap-3 rounded-3xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-800 dark:text-amber-300">
      <ShieldAlert className="h-5 w-5 shrink-0" />
      <span className="flex-1">{t("sec.tempBanner")}</span>
      <span className="font-bold underline">{t("sec.changeNow")}</span>
    </Link>
  );
};

export const AccountSecurity = () => {
  const { t } = usePrefs();
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const copy = () => navigator.clipboard?.writeText(user.username || "").then(() => toast.success(t("sec.copied"))).catch(() => {});
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (f.next !== f.confirm) return toast.error(t("sec.mismatch"));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password/change", { current_password: f.current, new_password: f.next });
      setUser(data);
      setF({ current: "", next: "", confirm: "" });
      toast.success(t("sec.changed"));
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  const field = (k: "current" | "next" | "confirm", label: string, auto: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={`pw-${k}`}>{t(label)}</Label>
      <Input id={`pw-${k}`} type="password" dir="ltr" required minLength={k === "current" ? 1 : 8} maxLength={128} autoComplete={auto}
        value={f[k]} onChange={(e: any) => setF({ ...f, [k]: e.target.value })} data-testid={`password-${k}-input`} className="h-11 rounded-2xl" />
    </div>
  );
  return (
    <section id="security" data-testid="account-security-card" className="rounded-3xl border bg-card p-5 space-y-4 scroll-mt-24">
      {user.username && (
        <div className="flex items-center gap-3 rounded-2xl bg-muted p-3">
          <span className="grid place-items-center h-10 w-10 rounded-2xl bg-primary/10 text-primary"><AtSign className="h-5 w-5" /></span>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">{t("sec.username")}</p>
            <p data-testid="account-username" dir="ltr" className="font-display text-xl font-black truncate text-start">{user.username}</p>
          </div>
          <button type="button" onClick={copy} data-testid="copy-username-button" className="h-9 px-3 rounded-full border text-xs font-bold inline-flex items-center gap-1.5 hover:bg-accent">
            <Copy className="h-3.5 w-3.5" />{t("sec.copy")}
          </button>
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><KeyRound className="h-5 w-5" />{t("sec.title")}</h2>
        {user.temp_password && <p data-testid="temp-password-note" className="rounded-2xl bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2 text-xs font-semibold">{t("sec.tempNote")}</p>}
        {field("current", "sec.current", "current-password")}
        {field("next", "sec.new", "new-password")}
        {field("confirm", "sec.confirm", "new-password")}
        <button disabled={busy} data-testid="password-change-submit" className="h-11 px-6 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60 active:scale-95 transition-transform">
          {busy ? t("common.loading") : t("sec.save")}
        </button>
      </form>
    </section>
  );
};
